import { ref } from 'vue'
import { isArray } from '@/global/utils/utils'
import { useWorkOrderStreetPaths } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/composables/useWorkOrderStreetPaths'
import { useWorkOrderIrregularities } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/composables/useWorkOrderIrregularities'
import { useWorkOrderVehiclePositions } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/composables/useWorkOrderVehiclePositions'
import { toTaskId } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/composables/workOrderData'

/**
 * Everything a work order's map draws, for whoever is drawing it.
 *
 * Two screens draw the same picture: the listing, beside the rows the user marked, and
 * the order's own "Podaci o izvršenju". A route on its own is only where the order was
 * meant to go - what makes it worth looking at is how much of it was actually driven and
 * what was reported along the way, and neither of those comes with the order. They are
 * fetched the same way for both, from here, so the two maps cannot end up showing
 * different things about the same order.
 *
 * The routes come with their coverage, which is what asks for the orders as well as
 * their tasks: the task says where the route runs, the orders say how much of it was
 * driven. The details screen used to ask for the task alone, which is why its map drew
 * the route in one flat colour while the listing's drew it in three.
 *
 * Where the vehicle itself has got to comes with them - the route says where it was meant
 * to go, the position says where it is. It is the one part of this that is not remembered:
 * see `useWorkOrderVehiclePositions`.
 */
export function useWorkOrderMapData () {
  const { isFetching, loadStreets, cancelPending } = useWorkOrderStreetPaths()
  const { loadIrregularities } = useWorkOrderIrregularities()
  const { loadVehiclePositions } = useWorkOrderVehiclePositions()

  const streets = ref([])
  const irregularities = ref([])
  const vehicles = ref([])
  // Whether any of the three is still on its way. `isFetching` above speaks for the
  // routes alone, and a screen that puts a loader over itself has to keep it up until
  // there is nothing left to draw.
  const isLoading = ref(false)
  // Which orders the lists above describe. Asking for the same ones again - a panel
  // hidden and shown, a tab left and come back to - is not a reason to fetch them twice.
  let loadedKey = ''

  const clear = () => {
    // Whatever is on its way describes orders nobody is looking at any more.
    cancelPending()
    streets.value = []
    irregularities.value = []
    vehicles.value = []
    loadedKey = ''
  }

  /**
   * Loads what the given orders bring to a map. One order or a marked page of them - the
   * two screens differ only in how many they hand over.
   *
   * `force` asks for the same orders again: what an order's map draws keeps changing
   * while the order is being driven, so a refresh has to go past the guard below.
   */
  const load = async (records, options) => {
    const { force = false } = options || {}
    const orders = (isArray(records) ? records : []).filter(record => !!record?.id)

    if (!orders.length) {
      clear()

      return
    }

    const key = orders.map(record => record.id).join(',')

    if (key === loadedKey && !force) {
      return
    }

    const taskIds = orders.map(toTaskId).filter(Boolean)
    const orderIds = orders.map(record => record.id)

    try {
      isLoading.value = true

      const [loadedStreets, loadedIrregularities, loadedVehicles] = await Promise.all([
        loadStreets(taskIds, orderIds),
        loadIrregularities(orders),
        loadVehiclePositions(orders)
      ])

      streets.value = loadedStreets
      irregularities.value = loadedIrregularities
      vehicles.value = loadedVehicles
      loadedKey = key
    }
    finally {
      isLoading.value = false
    }
  }

  return {
    streets,
    irregularities,
    vehicles,
    isFetching,
    isLoading,
    load,
    clear
  }
}
